import { useDebounce } from '@shared/utils';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Item, UseAutocompleteSearchProps } from '../types';

function useAutocompleteSearch({ searchApi, debounceMs = 300, fixedItems, freeSolo, onValueChange, onChange, open }: UseAutocompleteSearchProps) {
  const [inputValue, setInputValue] = useState('');
  const [apiItems, setApiItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Clear inputValue when dropdown opens to ensure the correct item is highlighted.
  // This avoids issues when multiple items share the same display value (e.g., '+1' for several countries).
  useEffect(() => {
    if (open) {
      setInputValue('');
    }
  }, [open]);

  // Debounce search input to avoid excessive API calls
  const debouncedSearch = useDebounce(inputValue, debounceMs);

  // Handle search input change
  const handleSearchChange = useCallback(
    (search: string) => {
      setInputValue(search);

      // If freeSolo is enabled and search is not empty, we can set the value directly
      if (freeSolo && search && !fixedItems.some(item => item.value === search)) {
        // Call appropriate callback
        if (onValueChange) {
          onValueChange(search);
        } else if (onChange) {
          onChange(search);
        }
      }
    },
    [freeSolo, fixedItems, onChange, onValueChange]
  );

  // Fetch items from API when search changes
  useEffect(() => {
    if (!searchApi) return;

    let isMounted = true;
    const fetchData = async () => {
      if (!isMounted) return;

      setLoading(true);
      setError(null);
      try {
        const data = await searchApi(debouncedSearch);

        if (!isMounted) return;

        // Filter out any API items that match fixed items to avoid duplicates
        const uniqueApiItems = data.filter(apiItem => !fixedItems.some(fixedItem => fixedItem.value === apiItem.value));

        setApiItems(uniqueApiItems);
      } catch (error) {
        if (isMounted) {
          setError(error instanceof Error ? error : new Error('Unknown error'));
          setApiItems([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    if (debouncedSearch) {
      fetchData();
    } else {
      setApiItems([]);
    }

    return () => {
      isMounted = false;
    };
  }, [debouncedSearch, searchApi, fixedItems]);

  // Combine fixed and API items
  const allItems = useMemo(() => {
    return [...fixedItems, ...apiItems];
  }, [fixedItems, apiItems]);

  return {
    inputValue,
    apiItems,
    loading,
    error,
    debouncedSearch,
    handleSearchChange,
    allItems,
  };
}

export { useAutocompleteSearch };
